/** @jsx jsx */
/** @jsxFrag React.Fragment */
import {useCallback, useEffect, useRef, useState} from 'react'
import {Transition} from 'react-transition-group'
import {useDispatch} from 'react-redux'
import {useIntl} from 'react-intl'
import {css, jsx} from '@emotion/core'
import styled from '@emotion/styled'
import {toggle} from 'opticks/lib/optimizely'
import noop from 'lodash/noop'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import {getCookie} from '@daedalus/core/src/utils/cookies'
import {MdClose as CloseIcon, MdDone as DoneIcon} from 'react-icons/md'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import Button from '../../../designSystem/Button'
import Grid from '../../../designSystem/helpers/Grid'
import {mq} from '../../../../utils/breakpoints'
import PageWidthContainer from '../../../designSystem/helpers/PageWidthContainer'
import {useOnClickOutside} from '../../../../hooks/useOnClickOutside'
import {useDeviceLayout} from '../../../../context/deviceLayout'
import transitions from '../../../../utils/transitions'
import {CookieBannerProClickComponent, useCookiePro} from './useCookiePro'

const CookieBannerWrapper = styled('div')(
  ({theme}) => css`
    bottom: 0;
    width: 100%;
    position: fixed;
    background: ${theme.colors.cookieProBannerBackground};
    color: ${theme.colors.cookieProBannerColor};
    z-index: 100;
    padding: ${theme.layout.spacing.s500}px 0;
    border-top-left-radius: ${theme.layout.radius.lg}px;
    border-top-right-radius: ${theme.layout.radius.lg}px;
    box-shadow: ${theme.shadows.modal};
    transition: opacity 500ms ease-in-out;

    ${mq.desktopXs(`
      padding: ${theme.layout.spacing.s600}px 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    `)};
  `
)

const GridWrapper = styled('div')(
  () => css`
    ${mq.desktopXs(`padding: 0 56px 0 0;`)};
  `
)

const BannerTitle = styled('h2')(
  ({theme}) => css`
    ${theme.typography.heading.title3};
    margin: 0;

    ${mq.desktopXs(theme.typography.heading.title2)};
  `
)

const BannerTitleIcon = styled('img')(
  ({theme}) => css`
    margin-right: ${theme.layout.spacing.s300}px;
    vertical-align: middle;

    ${mq.desktopXs(`
      width: 32px;
    `)};
  `
)

const BannerText = styled('p')(
  ({theme}) => css`
    ${theme.typography.text.small};
    margin: ${theme.layout.spacing.s300}px 0 ${theme.layout.spacing.s500}px 0;

    ${mq.desktopXs(css`
      ${theme.typography.text.regular};
      margin: ${theme.layout.spacing.s500}px 0 ${theme.layout.spacing.s500}px 0;
    `)};
  `
)

const SettingsLink = styled('a')(
  ({theme}) => css`
    ${theme.typography.text.small};
    text-decoration: underline;
    color: ${theme.colors.cookieProBannerColor};

    &:hover,
    &:focus {
      color: ${theme.colors.cookieProBannerColor};
    }

    ${mq.desktopXs(css`
      ${theme.typography.text.regular};
    `)};
  `
)

const CloseButton = styled(Button)(
  ({theme}) => css`
    height: 24px;
    position: absolute;
    top: 0;
    right: ${theme.layout.spacing.s500}px;

    ${mq.desktopXs(css`
      height: 32px;
    `)};
  `
)

const ActionButton = styled(Button)(
  () => css`
    width: 100%;

    ${mq.desktopXs(css`
      width: 180px;
    `)};
  `
)

const PageWidthContainerElement = styled(PageWidthContainer)`
  position: relative;
`

const shouldCookieBannerBeShown = (): boolean => {
  // if `OptanonAlertBoxClosed` cookie exists then they have interacted with the
  // banner or the cookie is expired and reconsent will occur
  const userHasSavedCookiePreferences = getCookie('OptanonAlertBoxClosed')
  // if CookiePro script fails for some reason (window.OneTrust === undefined) don't show the banner
  const shouldShowBanner = Boolean(
    !userHasSavedCookiePreferences && window.OneTrust
  )

  return shouldShowBanner
}

/**
 * Custom implementation of a GDPR compliant cookie banner. It makes use
 * of the OneTrust SDK, so make sure the SDK scripts are correctly loaded
 * in the HTML template.
 */
export interface CookieBannerProps {
  /**
   * Public url where assets are loaded. Used to load illustrations.
   */
  publicUrl?: string
  /**
   * Callback invoked when a user accepts the cookies, either in the
   * banner or the preference center
   */
  onAccept?: (acceptedByUser: boolean) => void
}

export const CookieBanner = ({
  publicUrl = '/',
  onAccept: setCookiesAcceptedByUser = noop
}: CookieBannerProps) => {
  const dispatch = useDispatch()
  const {locale} = useIntl()
  const ref = useRef()
  const {isMobile} = useDeviceLayout()

  const [isCookieBannerVisible, setIsCookieBannerVisible] = useState(false)

  const onAcceptCookies = useCallback(() => {
    setIsCookieBannerVisible(false)
    setCookiesAcceptedByUser(true)
  }, [setIsCookieBannerVisible, setCookiesAcceptedByUser])

  const {
    trackBannerSubmitted,
    openPreferencesCenter,
    cookieConsentContext,
    isPreferencesCenterVisible
  } = useCookiePro(onAcceptCookies)

  const handleCookieProScriptLoaded = useCallback(() => {
    setIsCookieBannerVisible(shouldCookieBannerBeShown)
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.CookieBannerScript,
        action: Action.Loaded
      })
    )
  }, [setIsCookieBannerVisible, dispatch])

  const handleAcceptAllCookies = useCallback(() => {
    // eslint-disable-next-line new-cap
    window.OneTrust?.AllowAll()
    setIsCookieBannerVisible(false)
    setCookiesAcceptedByUser(true)
    trackBannerSubmitted(CookieBannerProClickComponent.AcceptButton)
  }, [setCookiesAcceptedByUser, trackBannerSubmitted])

  const handleRejectAllCookies = useCallback(
    (component: CookieBannerProClickComponent) => {
      if (isCookieBannerVisible) {
        // eslint-disable-next-line new-cap
        window.OneTrust?.RejectAll()
        setIsCookieBannerVisible(false)
        setCookiesAcceptedByUser(false)
        trackBannerSubmitted(component)
      }
    },
    [setCookiesAcceptedByUser, trackBannerSubmitted, isCookieBannerVisible]
  )

  const trackBannerShown = () => {
    if (!isCookieBannerVisible) return

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.CookieBanner,
        action: Action.Displayed,
        analyticsContext: {
          [AnalyticsContext.CookieConsentContext]: cookieConsentContext
        }
      })
    )
  }

  useOnClickOutside(ref, () => {
    if (!isPreferencesCenterVisible) {
      handleRejectAllCookies(CookieBannerProClickComponent.AppPage)
    }
  })

  useEffect(() => {
    if (window.COOKIE_PRO_LOADED) {
      handleCookieProScriptLoaded()
      return
    }

    // sometimes window.OneTrust is undefined as the script takes a while to load
    // so we should listen for the event here too
    window.addEventListener('cookieProLoaded', handleCookieProScriptLoaded)

    return () => {
      window.removeEventListener('cookieProLoaded', handleCookieProScriptLoaded)
    }
  }, [handleCookieProScriptLoaded])

  useEffect(trackBannerShown, [isCookieBannerVisible, dispatch])
  useEffect(() => window.OneTrust?.changeLanguage(locale), [locale])

  return (
    <Transition
      in={isCookieBannerVisible}
      timeout={500}
      classNames={transitions.fadeInOutAndHide}
      appear={isCookieBannerVisible}
    >
      {(state: string) => (
        <CookieBannerWrapper
          ref={ref}
          css={transitions.fadeInOutAndHide[state]}
        >
          <PageWidthContainerElement
            paddingMobileSmSize="s400"
            paddingMobileLgSize="s500"
            paddingDesktopXsSize={toggle('dq82hd-desktop-map', 's500', 's600')}
          >
            <CloseButton
              appearance="noStyle"
              onClick={() =>
                handleRejectAllCookies(
                  CookieBannerProClickComponent.CloseButton
                )
              }
            >
              <CloseIcon size={isMobile ? 24 : 32} />
            </CloseButton>

            <GridWrapper>
              <Grid container alignItems="center">
                <Grid mobileSm={12} desktopXs="fill">
                  <BannerTitle>
                    <BannerTitleIcon
                      alt=""
                      src={`${publicUrl}cookie-banner/cookie-ico.svg`}
                    />
                    <FormattedMessageWrapper
                      id="cookieProBannerTitle"
                      defaultMessage="We use cookies"
                    />
                  </BannerTitle>
                  <BannerText>
                    <FormattedMessageWrapper
                      id="cookieProBannerMessage"
                      defaultMessage="We use cookies to give you the best experience on our website and also for analytics purposes."
                    />{' '}
                    <SettingsLink onClick={openPreferencesCenter}>
                      <FormattedMessageWrapper
                        id="cookieProBannerSettingsLink"
                        defaultMessage="Learn more and adjust settings."
                      />
                    </SettingsLink>
                  </BannerText>
                </Grid>
                <Grid mobileSm={12} desktopXs="fit">
                  <Grid container>
                    <Grid mobileSm={6}>
                      <ActionButton
                        fullWidth
                        dataId="acceptAllCookiesButton"
                        size="lg"
                        appearance="white"
                        onClick={handleAcceptAllCookies}
                      >
                        <DoneIcon size={24} />
                        <FormattedMessageWrapper
                          id="acceptAllCookiesButton"
                          defaultMessage="Accept"
                        />
                      </ActionButton>
                    </Grid>

                    <Grid mobileSm={6}>
                      <ActionButton
                        fullWidth
                        dataId="rejectAllCookiesButton"
                        size="lg"
                        appearance="white"
                        onClick={() =>
                          handleRejectAllCookies(
                            CookieBannerProClickComponent.RejectButton
                          )
                        }
                      >
                        <CloseIcon size={24} />
                        <FormattedMessageWrapper
                          id="rejectAllCookiesButton"
                          defaultMessage="Reject"
                        />
                      </ActionButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </GridWrapper>
          </PageWidthContainerElement>
        </CookieBannerWrapper>
      )}
    </Transition>
  )
}
